import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type Category = "power_bi" | "sql" | "python";
type Difficulty = "easy" | "medium" | "hard";

interface Challenge {
  id: string;
  category: Category;
  difficulty: Difficulty;
  title: string;
  description: string;
  starter_code: string;
  order_index: number;
}

interface EvaluationResult {
  isCorrect: boolean;
  feedback: string;
  suggestions: string[];
  pointsEarned?: number;
  firstTime?: boolean;
  newTotalPoints?: number | null;
  newLevel?: number | null;
}

interface CategoryStats {
  total: number;
  completed: number;
  percentage: number;
  eligible: boolean;
}

export function useChallenges(category?: Category, difficulty?: Difficulty) {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [allChallenges, setAllChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    fetchChallenges();
  }, [category, difficulty]);

  async function fetchChallenges() {
    setLoading(true);

    // Fetch filtered challenges
    let query = supabase
      .from("coding_challenges")
      .select("id, category, difficulty, title, description, starter_code, order_index")
      .eq("is_active", true)
      .eq("status", "approved")
      .order("order_index");

    if (category) query = query.eq("category", category);
    if (difficulty) query = query.eq("difficulty", difficulty);

    // Fetch ALL challenges (unfiltered) for global stats
    const allQuery = supabase
      .from("coding_challenges")
      .select("id, category, difficulty, title, description, starter_code, order_index")
      .eq("is_active", true)
      .eq("status", "approved")
      .order("order_index");

    const [filteredResult, allResult] = await Promise.all([query, allQuery]);

    if (filteredResult.error) {
      console.error("Error fetching challenges:", filteredResult.error);
    } else {
      setChallenges((filteredResult.data as Challenge[]) || []);
    }

    if (!allResult.error) {
      setAllChallenges((allResult.data as Challenge[]) || []);
    }

    // Fetch completed
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: attempts } = await supabase
        .from("user_challenge_attempts")
        .select("challenge_id")
        .eq("user_id", user.id)
        .eq("is_correct", true);

      if (attempts) {
        setCompletedIds(new Set(attempts.map((a: { challenge_id: string }) => a.challenge_id)));
      }
    }

    setLoading(false);
  }

  // Compute per-category stats from ALL challenges (not filtered)
  const categoryStats: Record<Category, CategoryStats> = (() => {
    const categories: Category[] = ["python", "sql", "power_bi"];
    const stats = {} as Record<Category, CategoryStats>;

    for (const cat of categories) {
      const total = allChallenges.filter(c => c.category === cat).length;
      const completed = allChallenges.filter(c => c.category === cat && completedIds.has(c.id)).length;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      stats[cat] = { total, completed, percentage, eligible: percentage >= 80 };
    }

    return stats;
  })();

  const globalStats = {
    total: allChallenges.length,
    completed: allChallenges.filter(c => completedIds.has(c.id)).length,
    get percentage() { return this.total > 0 ? Math.round((this.completed / this.total) * 100) : 0; },
  };

  async function evaluateCode(challengeId: string, userCode: string): Promise<EvaluationResult | null> {
    try {
      const { data, error } = await supabase.functions.invoke("evaluate-challenge", {
        body: { challenge_id: challengeId, user_code: userCode },
      });

      if (error) throw error;

      if (data.isCorrect) {
        setCompletedIds((prev) => new Set([...prev, challengeId]));
      }

      if (data.pointsEarned && data.pointsEarned > 0) {
        toast({
          title: `🎉 +${data.pointsEarned} pts no ranking!`,
          description: "Pontos adicionados pelo seu acerto.",
        });
      }

      return data as EvaluationResult;
    } catch (err) {
      console.error("Evaluation error:", err);
      toast({
        title: "Erro ao avaliar",
        description: "Tente novamente em alguns segundos.",
        variant: "destructive",
      });
      return null;
    }
  }

  return { challenges, loading, completedIds, evaluateCode, refetch: fetchChallenges, categoryStats, globalStats };
}
